import { QuestionnaireQuestions } from '../../models';

type Result<Questionnaire extends QuestionnaireQuestions> = Record<
  keyof Questionnaire,
  number
>;

export const calculationOperators = [
  'ADD',
  'SUBTRACT',
  'MULTIPLY',
  'DIVIDE',
] as const;

export type Calculation = (typeof calculationOperators)[number];

export type CalculationDefinition = [
  Calculation,
  (
    | { type: 'static'; value: number }
    | { type: 'dynamic'; key: string }
    | CalculationDefinition
  )[]
];

export type CalculationField<FieldId = string> = {
  field: FieldId;
  calculations: CalculationDefinition;
  initialValue?: number;
};

export type CalculationFields<
  Questionnaire extends QuestionnaireQuestions,
  FieldId = keyof Questionnaire
> = CalculationField<FieldId>[];

export const calculate = (
  operator: Calculation,
  value1: number,
  value2: number
) => {
  switch (operator) {
    case 'ADD':
      return value1 + value2;
    case 'SUBTRACT':
      return value1 - value2;
    case 'MULTIPLY':
      return value1 * value2;
    case 'DIVIDE':
      return value1 / value2;
  }
};

/**
 *
 * `calculate` expects the logic, the data and an initial result shape
 * and then runs the calculations.
 *
 * @example
 * const calculation: CalculationField[] = [
 *     {
 *       field: 'bmi',
 *       calculations: [
 *         'MULTIPLY',
 *         [
 *           {
 *             key: 'height',
 *             type: 'dynamic',
 *           },
 *           {
 *             key: 'weight',
 *             type: 'dynamic',
 *           },
 *         ],
 *       ],
 *     },
 *
 *     {
 *       field: 'insuranceDifference',
 *       calculations: [
 *         'SUBTRACT',
 *         [
 *           {
 *             key: 'insuredSum',
 *             type: 'dynamic',
 *           },
 *           [
 *             'MULTIPLY',
 *             [
 *               {
 *                 key: 'weight',
 *                 type: 'dynamic',
 *               },
 *               {
 *                 type: 'static',
 *                 value: 10,
 *               },
 *             ],
 *           ],
 *         ],
 *       ],
 *     },
 *   ];
 *
 *   const answers = {
 *     height: 180,
 *     wight: 100,
 *     insuredSum: 300000,
 *     income: 20000,
 *   };
 *
 *   calculateFields(calculation, answers)); // {bmi: 24.69, insuranceDifference: 100000}
 */
export const calculateFields = <Questionnaire extends QuestionnaireQuestions>(
  calculationLogic: CalculationFields<Questionnaire>,
  data: Partial<Questionnaire>
): Result<Partial<Questionnaire>> => {
  return calculationLogic.reduce(
    (acc, logic): Result<Partial<Questionnaire>> => {
      const { field, calculations } = logic;
      return {
        ...acc,
        [field]: Math.round(runCalculation(calculations, data) * 100) / 100,
      };
    },
    {} as Result<Partial<Questionnaire>>
  );
};

export const runCalculation = <Data extends QuestionnaireQuestions>(
  calculation: CalculationDefinition,
  data: Data
): number => {
  const [operator, definitions] = calculation;

  return (
    definitions.reduce((acc: number | null, def) => {
      if (Array.isArray(def)) {
        return acc === null
          ? runCalculation(def, data)
          : calculate(operator, acc, runCalculation(def, data));
      }
      if (def.type === 'dynamic') {
        const referencedValue = data[def.key];
        const value = typeof referencedValue === 'number' ? referencedValue : 0;
        return acc === null ? value : calculate(operator, acc, value);
      } else if (def.type === 'static') {
        const value = def.value ?? 0;
        return acc === null ? value : calculate(operator, acc, value);
      }
      return acc;
    }, null) ?? 0
  );
};
